import {
  DEFAULT_LIMITS,
  type CanonicalValues,
  type FieldDefinition,
  type FieldError,
  type FieldRules,
  type FieldType,
  type FieldValue,
  type FormSchema,
  type ValidateOptions,
  type ValidationResult,
} from './types.ts';
import { phoneRegionsOf } from './phone-regions.ts';

/** FR-5.7 — the rules that apply to each field type. The conformance vectors cover every cell. */
export const RULES_BY_TYPE: Record<FieldType, readonly (keyof FieldRules)[]> = {
  text: ['minLength', 'maxLength', 'pattern'],
  textarea: ['minLength', 'maxLength', 'pattern'],
  email: ['maxLength'],
  phone: ['phoneRegions', 'phoneRegion'],
  number: ['min', 'max'],
  select: ['options'],
  multiselect: ['options', 'maxItems'],
  checkbox: [],
  consent: [],
  date: ['minDate', 'maxDate'],
  url: ['maxLength'],
  hidden: ['maxLength', 'pattern'],
};

/** FR-6.3 — control fields are underscore-prefixed and never collide with real fields. */
export const CONTROL_PREFIX = '_';

export function isControlField(key: string): boolean {
  return key.startsWith(CONTROL_PREFIX);
}

/** Splits a raw request bag into the form's fields and the reserved control fields. */
export function splitControlFields(bag: Record<string, unknown>): {
  fields: Record<string, unknown>;
  control: Record<string, unknown>;
} {
  // Prototype-free, so a field named `toString` or `__proto__` is only ever a field.
  // cast: Object.create is typed as returning `any`; a null-prototype object is a string-keyed record.
  const fields = Object.create(null) as Record<string, unknown>;
  // cast: as above.
  const control = Object.create(null) as Record<string, unknown>;
  for (const [key, value] of Object.entries(bag)) {
    if (isControlField(key)) control[key] = value;
    else fields[key] = value;
  }
  return { fields, control };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const PHONE_CHARS = /^\+?[\d\s\-().]+$/u;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const TRUE_STRINGS = new Set(['on', 'true', '1', 'yes']);
const FALSE_STRINGS = new Set(['off', 'false', '0', 'no', '']);

type Coerced = { ok: true; value: FieldValue } | { ok: false; error: Omit<FieldError, 'field'> };

const ok = (value: FieldValue): Coerced => ({ ok: true, value });
const fail = (code: FieldError['code'], params?: FieldError['params']): Coerced =>
  params === undefined ? { ok: false, error: { code } } : { ok: false, error: { code, params } };

function isEmpty(raw: unknown): boolean {
  if (raw === undefined || raw === null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  if (Array.isArray(raw)) return raw.length === 0;
  return false;
}

/**
 * Made on first use, not when the module loads: browsers without `Intl.Segmenter` (Firefox before 125,
 * Safari before 14.1) run this module too — in the SDK sites install (spec §19 #73) — and a module that
 * throws while loading breaks every page that imports it.
 */
let graphemes: Intl.Segmenter | null | undefined;

function length(value: string): number {
  // Counts user-perceived characters: an emoji — even a composite one — is one character.
  graphemes ??= typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
  // Without it, code points: the same count except for composite emoji, which then count as several.
  if (graphemes === null) return Array.from(value).length;
  let count = 0;
  for (const _segment of graphemes.segment(value)) count += 1;
  return count;
}

function asString(raw: unknown): string | undefined {
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return undefined;
}

function compilePattern(pattern: string): RegExp | undefined {
  try {
    return new RegExp(`^(?:${pattern})$`, 'u');
  } catch {
    // An invalid pattern is a schema error, reported by validateFormSchema; never a runtime throw.
    return undefined;
  }
}

function checkLength(value: string, rules: FieldRules): Coerced | undefined {
  const len = length(value);
  if (rules.minLength !== undefined && len < rules.minLength) return fail('too_short', { min: rules.minLength });
  if (rules.maxLength !== undefined && len > rules.maxLength) return fail('too_long', { max: rules.maxLength });
  return undefined;
}

function isValidIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function coerce(field: FieldDefinition, raw: unknown, options: ValidateOptions): Coerced {
  const rules = field.rules ?? {};
  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'hidden': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      const lengthError = checkLength(value, rules);
      if (lengthError) return lengthError;
      if (rules.pattern !== undefined) {
        const pattern = compilePattern(rules.pattern);
        if (pattern && !pattern.test(value)) return fail('pattern');
      }
      return ok(value);
    }
    case 'email': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      if (length(value) > 254 || !EMAIL_PATTERN.test(value)) return fail('invalid_email');
      const lengthError = checkLength(value, rules);
      if (lengthError) return lengthError;
      return ok(value);
    }
    case 'phone': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      const digits = value.replace(/\D/gu, '').length;
      if (!PHONE_CHARS.test(value) || digits < 5 || digits > 15) return fail('invalid_phone');
      const normalize = options.normalizers?.phone;
      if (!normalize) return ok(value);
      const normalized = normalize(value, phoneRegionsOf(rules));
      return normalized === null ? fail('invalid_phone') : ok(normalized);
    }
    case 'number': {
      const value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw.trim()) : Number.NaN;
      if (!Number.isFinite(value)) return fail('invalid_number');
      if (rules.min !== undefined && value < rules.min) return fail('too_small', { min: rules.min });
      if (rules.max !== undefined && value > rules.max) return fail('too_large', { max: rules.max });
      return ok(value);
    }
    case 'select': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      if (rules.options && !rules.options.includes(value)) return fail('not_an_option');
      return ok(value);
    }
    case 'multiselect': {
      const list = Array.isArray(raw) ? raw : [raw];
      const values: string[] = [];
      for (const item of list) {
        const value = asString(item);
        if (value === undefined) return fail('invalid_type');
        values.push(value);
      }
      if (rules.maxItems !== undefined && values.length > rules.maxItems) return fail('too_many_items', { max: rules.maxItems });
      if (rules.options) {
        const allowed = rules.options;
        if (values.some((value) => !allowed.includes(value))) return fail('not_an_option');
      }
      return ok(values);
    }
    case 'checkbox':
    case 'consent': {
      if (typeof raw === 'boolean') return ok(raw);
      if (typeof raw === 'string') {
        const value = raw.trim().toLowerCase();
        if (TRUE_STRINGS.has(value)) return ok(true);
        if (FALSE_STRINGS.has(value)) return ok(false);
      }
      return fail('invalid_type');
    }
    case 'date': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      if (!isValidIsoDate(value)) return fail('invalid_date');
      if (rules.minDate !== undefined && value < rules.minDate) return fail('date_too_early', { min: rules.minDate });
      if (rules.maxDate !== undefined && value > rules.maxDate) return fail('date_too_late', { max: rules.maxDate });
      return ok(value);
    }
    case 'url': {
      const value = asString(raw);
      if (value === undefined) return fail('invalid_type');
      let protocol: string;
      try {
        protocol = new URL(value).protocol;
      } catch {
        return fail('invalid_url');
      }
      if (protocol !== 'http:' && protocol !== 'https:') return fail('invalid_url');
      const lengthError = checkLength(value, rules);
      if (lengthError) return lengthError;
      return ok(value);
    }
  }
}

/** Accepts only flat values: a primitive, or a list of strings. Anything nested is rejected. */
function coerceExtra(raw: unknown): FieldValue | undefined {
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'boolean') return raw;
  if (Array.isArray(raw) && raw.every((item): item is string => typeof item === 'string')) return raw.map((item) => item.trim());
  return undefined;
}

function exceedsValueLength(raw: unknown, max: number): boolean {
  if (typeof raw === 'string') return raw.length > max;
  // One level only: a list inside a list is never a valid value, and walking arbitrarily deep input
  // recursively would let a small payload overflow the stack.
  if (Array.isArray(raw)) return raw.length > max || raw.some((item) => typeof item === 'string' && item.length > max);
  return false;
}

/**
 * Validates one submission against a form schema (FR-5.5 – FR-5.9). Never throws: any input,
 * however malformed, produces a result. The same function runs in ingest and in the browser; the
 * only difference is the injected `normalizers`.
 *
 * Control fields (underscore-prefixed) are ignored — callers split them off with
 * `splitControlFields` and handle them in the guard pipeline.
 */
export function validateSubmission(
  schema: FormSchema,
  input: Record<string, unknown>,
  options: ValidateOptions = {},
): ValidationResult {
  const limits = options.limits ?? DEFAULT_LIMITS;
  const policy = options.unknownFieldPolicy ?? 'store_as_extra';
  const entries = Object.entries(input).filter(([key]) => !isControlField(key));

  if (entries.length > limits.maxFields) return { ok: false, reason: 'too_large', limit: 'maxFields' };
  if (entries.some(([, raw]) => exceedsValueLength(raw, limits.maxValueLength))) {
    return { ok: false, reason: 'too_large', limit: 'maxValueLength' };
  }

  const errors: FieldError[] = [];
  const values: Record<string, FieldValue> = {};
  // cast: a prototype-free record, so an extra field named `__proto__` is stored, not interpreted.
  const extra = Object.create(null) as Record<string, FieldValue>;
  const canonical: CanonicalValues = {};
  const known = new Set<string>();
  const submitted = new Map(entries);

  for (const field of schema.fields) {
    known.add(field.key);
    const raw = submitted.get(field.key);

    if (isEmpty(raw)) {
      if (field.required === true) errors.push({ field: field.key, code: field.type === 'consent' ? 'must_accept' : 'required' });
      continue;
    }

    const result = coerce(field, raw, options);
    if (!result.ok) {
      errors.push({ field: field.key, ...result.error });
      continue;
    }
    if (field.required === true && result.value === false) {
      // An unchecked required checkbox or consent is "not provided", not a valid `false`.
      errors.push({ field: field.key, code: field.type === 'consent' ? 'must_accept' : 'required' });
      continue;
    }

    values[field.key] = result.value;
    if (field.role !== undefined) canonical[field.role] = result.value;
  }

  for (const [key, raw] of entries) {
    if (known.has(key)) continue;
    if (policy === 'ignore') continue;
    if (policy === 'reject') {
      errors.push({ field: key, code: 'unknown_field' });
      continue;
    }
    if (isEmpty(raw)) continue;
    const value = coerceExtra(raw);
    if (value === undefined) errors.push({ field: key, code: 'invalid_type' });
    else extra[key] = value;
  }

  if (errors.length > 0) return { ok: false, reason: 'invalid', errors };
  return { ok: true, values, extra, canonical };
}
